import {
  ApiError,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  postNdjsonStream,
} from "@/api/client";
import type {
  AssistantEntityType,
  AssistantMessage,
  AssistantMessageFeedback,
  AssistantThread,
  StreamEvent,
} from "@/lib/assistant-ui-types";

/** Treat missing endpoints as empty while routes roll out. */
async function softGet<T>(path: string, fallback: T): Promise<T> {
  try {
    return await apiGet(path);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 404 || error.status === 501)) {
      return fallback;
    }
    throw error;
  }
}

export type AssistantPresetsResponse = {
  assistant_presets: Array<{
    id: string;
    type: string;
    icon: string;
    text: string;
    name: string;
    author_name: string;
    category: string;
    short_description?: string;
    long_description?: string;
    visibility: boolean;
    showcase?: boolean;
    autorun?: boolean;
  }>;
  order: string[];
  category_order: string[];
  prompt_paths: Record<string, string[]>;
};

export async function listAssistantPresets(): Promise<AssistantPresetsResponse> {
  return softGet("/api/assistant/presets", {
    assistant_presets: [],
    order: [],
    category_order: [],
    prompt_paths: {},
  });
}

export async function listAssistantThreads(): Promise<{
  threads: AssistantThread[];
}> {
  return softGet("/api/assistant/threads", { threads: [] });
}

export async function createAssistantThread(body?: {
  title?: string;
}): Promise<{ thread: AssistantThread }> {
  return apiPost("/api/assistant/threads", body ?? {});
}

export async function resolveEntityThread(body: {
  entityType: AssistantEntityType;
  entityId: string;
  stage?: string;
}): Promise<{ thread: AssistantThread; taskTitle?: string }> {
  return apiPost("/api/assistant/threads/entity", body);
}

export async function resolveCommandItemThread(body: {
  careerMoveId: string;
}): Promise<{
  thread: AssistantThread;
  taskTitle?: string;
  taskContext?: string;
}> {
  return apiPost("/api/assistant/threads/command-item", body);
}

export async function getAssistantThread(
  threadId: string,
  opts?: { startSeq?: number; limit?: number },
): Promise<{ thread: AssistantThread; messages: AssistantMessage[] }> {
  return apiGet(`/api/assistant/threads/${threadId}`, {
    startSeq: opts?.startSeq,
    limit: opts?.limit,
  });
}

export async function updateAssistantThread(
  threadId: string,
  body: {
    title?: string;
    status?: AssistantThread["status"];
  },
): Promise<{ thread: AssistantThread }> {
  return apiPatch(`/api/assistant/threads/${threadId}`, body);
}

export async function deleteAssistantThread(
  threadId: string,
): Promise<{ deleted: boolean }> {
  return apiDelete(`/api/assistant/threads/${threadId}`);
}

export async function setAssistantFeedback(
  threadId: string,
  messageId: string,
  feedback: AssistantMessageFeedback,
): Promise<{ message: AssistantMessage }> {
  return apiPatch(
    `/api/assistant/threads/${threadId}/messages/${messageId}/feedback`,
    { feedback },
  );
}

export async function getAssistantMemory(): Promise<{ memory: string }> {
  return apiGet("/api/assistant/memory");
}

export async function putAssistantMemory(
  memory: string,
): Promise<{ message: string }> {
  return apiPut("/api/assistant/memory", { memory });
}

export type AssistantStreamMeta = {
  threadId?: string;
  messageId?: string;
  durationMs?: number;
  verb?: string;
  steps?: string[];
};

export async function sendAssistantMessage(
  body: {
    message: string;
    threadId?: string;
    taskTitle?: string;
    taskContext?: string;
    stage?: string;
  },
  onEvent: (event: StreamEvent, meta: AssistantStreamMeta) => void,
  signal?: AbortSignal,
): Promise<AssistantStreamMeta> {
  const meta: AssistantStreamMeta = {};
  const path = body.threadId
    ? `/api/assistant/threads/${body.threadId}/messages`
    : "/api/assistant";

  await postNdjsonStream(
    path,
    {
      message: body.message,
      ...(body.taskTitle ? { taskTitle: body.taskTitle } : {}),
      ...(body.taskContext ? { taskContext: body.taskContext } : {}),
      ...(body.stage ? { stage: body.stage } : {}),
    },
    (raw) => {
      const event = raw as StreamEvent & Record<string, unknown>;
      if (event.type === "status" && event.label === "thread" && event.threadId) {
        meta.threadId = String(event.threadId);
      }
      if (event.type === "status" && event.label === "message_saved") {
        if (event.messageId) meta.messageId = String(event.messageId);
        if (typeof event.durationMs === "number") meta.durationMs = event.durationMs;
        if (typeof event.verb === "string") meta.verb = event.verb;
        if (Array.isArray(event.steps)) meta.steps = event.steps as string[];
      }
      onEvent(event as StreamEvent, meta);
    },
    signal,
  );

  return meta;
}
